import { z } from 'zod';
import { MULTI_SELECT_ENTITIY_LIMIT } from '@soundxyz/utils/const';
import { MAX_UPLOAD_RELEASE_PRICE_DECIMALS } from '@utils/helpers';
import { MAX_CHAR_LENGTH } from '../errors';

export const decimalsError = `Price must be fewer than ${
  MAX_UPLOAD_RELEASE_PRICE_DECIMALS + 1
} decimals`;

export const EditAuctionConfigurationErrors = z.object({
  presalePricePerMint: z.string().nullish(),
  publicPricePerMint: z.string().nullish(),
  presaleStartTime: z.string().nullish(),
  publicStartTime: z.string().nullish(),
  addresses: z.string().nullish(),
  manuallyAddedAllowlist: z.object({
    description: z.string().nullable(),
  }),
  collectorsOfReleases: z.string().nullable(),
  collectorsOfArtists: z.string().nullable(),
  presaleEndTime: z.string().nullish(),
  publicEndTime: z.string().nullish(),
  maxMintable: z.string().nullish(),
  publicMaxMintsPerWallet: z.string().nullish(),
  presaleMaxSupply: z.string().nullish(),
  presaleMaxMintsPerWallet: z.string().nullish(),
});

const EditAuctionConfigurationErrorsOptions = EditAuctionConfigurationErrors.keyof().options;
export type EditAuctionConfigurationErrorKeys =
  (typeof EditAuctionConfigurationErrorsOptions)[number];

export type EditAuctionConfigurationErrorsType = (typeof EditAuctionConfigurationErrors)['_input'];

type ErrorKeys = 'required_error' | 'description_too_long' | 'list_too_long';

export const EditErrorMessages: Record<
  EditAuctionConfigurationErrorKeys,
  Partial<Record<ErrorKeys, string>>
> = {
  publicPricePerMint: {
    required_error: 'Public price per mint is required',
  },
  presalePricePerMint: {
    required_error: 'Presale price per mint is required',
  },
  presaleStartTime: {
    required_error: 'Presale start time is required',
  },
  publicStartTime: {
    required_error: 'Public start time is required',
  },
  addresses: {
    required_error: 'Add one address or select collectors in the dropdown above to enable presale',
  },
  manuallyAddedAllowlist: {
    description_too_long: `Description must be fewer than ${MAX_CHAR_LENGTH['manuallyAddedDescription']} characters`,
  },
  presaleEndTime: {
    required_error: 'Presale end time is required',
  },
  publicEndTime: {
    required_error: 'Public end time is required',
  },
  maxMintable: {
    required_error: 'Total mint supply is required',
  },
  publicMaxMintsPerWallet: {
    required_error: 'Max mints per wallet is required',
  },
  presaleMaxSupply: {
    required_error: 'Total mint supply is required',
  },
  presaleMaxMintsPerWallet: {
    required_error: 'Max mints per wallet is required',
  },
  collectorsOfReleases: {
    list_too_long: `Can't add more than ${MULTI_SELECT_ENTITIY_LIMIT} releases to the presale allowlist`,
  },
  collectorsOfArtists: {
    list_too_long: `Can't add more than ${MULTI_SELECT_ENTITIY_LIMIT} artists to the presale allowlist`,
  },
};

export const initialEditAuctionConfigurationErrors = () =>
  ({
    presalePricePerMint: null,
    publicPricePerMint: null,
    presaleStartTime: null,
    publicStartTime: null,
    addresses: null,
    manuallyAddedAllowlist: {
      description: null,
    },
    collectorsOfArtists: null,
    collectorsOfReleases: null,
    presaleEndTime: null,
    publicEndTime: null,
    maxMintable: null,
    publicMaxMintsPerWallet: null,
    presaleMaxSupply: null,
    presaleMaxMintsPerWallet: null,
  }) satisfies EditAuctionConfigurationErrorsType;
